import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Loader2, ShieldCheck } from 'lucide-react';

// IYZ-CF-BUYER-01 — pre-checkout collection of the few billing details the
// payment provider requires and TreatX does not already hold. Name, e-mail,
// phone come from the canonical patient identity and are NOT re-asked here.
// Country IS asked when the identity holds none: the provider's billing
// contract requires it and an international patient often has no country on
// file. PSP-agnostic copy: no provider name appears.
const FIELD_LABEL = {
  identityNumber: 'identityNumber',
  registrationAddress: 'registrationAddress',
  city: 'city',
  country: 'country',
  name: 'name',
  surname: 'surname',
  email: 'email',
};

// Fields this dialog can collect. Everything else the server names belongs to
// the patient's canonical profile and is deliberately NOT writable from the
// payment endpoint, so asking for it here would be a dead end.
const COLLECTABLE = ['identityNumber', 'registrationAddress', 'city', 'country'];

export const BuyerDetailsDialog = ({ open, paymentId, missingFields = [], onComplete, onCancel }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    identity_type: 'passport', identity_number: '', registration_address: '',
    city: '', country: '', zip_code: '',
  });  // THE DEFECT: the dialog used to ignore `missing_fields` from the response and
  // show one generic sentence, while country visibility stayed frozen on the
  // ORIGINAL prop. A patient whose canonical surname is empty filled every
  // visible field, got "some required details are still missing", and had no way
  // to learn which detail or to fix it. The latest server answer now wins.
  const [serverMissing, setServerMissing] = useState(null);
  const effectiveMissing = serverMissing === null ? missingFields : serverMissing;
  const needsCountry = effectiveMissing.includes('country');
  const profileOwned = effectiveMissing.filter((f) => !COLLECTABLE.includes(f));
  const [countries, setCountries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  // IYZ-CF-BUYER-NAME-01 — a missing canonical surname used to end in "ask us to
  // correct your profile", which diagnoses the refusal without recovering the
  // checkout. The patient may now declare their OWN name here, through the
  // canonical fill-once writer, and carry on paying THIS payment.
  const nameBlocked = profileOwned.includes('surname') || profileOwned.includes('name');
  const [nameForm, setNameForm] = useState({ first_name: '', last_name: '' });
  const [nameSaving, setNameSaving] = useState(false);
  const [nameError, setNameError] = useState(null);

  // The 409 that opens this dialog carries no field list, so the canonical
  // requirements are read from the existing endpoint as soon as it opens: the
  // patient sees a missing surname immediately instead of after a wasted
  // submission that returns "some required details are still missing".
  React.useEffect(() => {
    if (!open || !paymentId) return;
    let alive = true;
    api.get(`/payments/${paymentId}/buyer-identity/requirements`)
      .then(({ data }) => {
        if (alive && Array.isArray(data?.missing_fields)) setServerMissing(data.missing_fields);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [open, paymentId]);

  // The existing public country catalogue — the same source the rest of the
  // product uses. No second list is introduced; the value is the ISO-2 code the
  // buyer contract stores.
  React.useEffect(() => {
    if (!needsCountry || countries.length) return;
    let alive = true;
    api.get('/public/catalog/countries')
      .then(({ data }) => { if (alive) setCountries(data?.countries || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [needsCountry, countries.length]);

  const labelFor = (f) => (FIELD_LABEL[f]
    ? t(`checkout.buyerDetails.${FIELD_LABEL[f]}`)
    : f);

  const set = (k) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [k]: value }));
    setFieldErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));
  };

  const submitName = async () => {
    setNameSaving(true);
    setNameError(null);
    try {
      const res = await api.put('/patient/profile/legal-name', {
        first_name: nameForm.first_name.trim(),
        last_name: nameForm.last_name.trim(),
      });
      const missing = res.data?.missing_fields || [];
      setServerMissing(missing);
      // The payment context is untouched: the same dialog, the same paymentId.
      // Nothing is submitted to the PSP until the billing form is complete.
      if (res.data?.complete) onComplete?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = detail?.code;
      setNameError(code === 'NAME_ALREADY_SET'
        ? t('checkout.buyerDetails.nameAlreadySet')
        : t('checkout.buyerDetails.nameInvalid'));
    } finally {
      setNameSaving(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      const payload = { ...form };
      if (!needsCountry || !payload.country) delete payload.country;
      if (!payload.zip_code) delete payload.zip_code;
      const res = await api.post(`/payments/${paymentId}/buyer-identity`, payload);
      const missing = res.data?.missing_fields || [];
      // Consume the LATEST answer, always — including when it is complete, so a
      // later re-render cannot fall back to the stale prop.
      setServerMissing(missing);
      if (res.data?.complete) {
        onComplete?.();
        return;
      }
      setError(null);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Field-level validation (422) is shown BESIDE the field it concerns; the
      // values the patient typed are never cleared.
      const perField = {};
      if (Array.isArray(detail)) {
        // The server speaks Pydantic; the patient does not. Each violation is
        // turned into localised wording that NAMES the field it concerns.
        const FORM_TO_CANONICAL = {
          identity_number: 'identityNumber',
          registration_address: 'registrationAddress',
          city: 'city',
          country: 'country',
          zip_code: 'zipCode',
        };
        for (const d of detail) {
          const key = (d?.loc || []).filter((p) => p !== 'body').pop();
          if (!key) continue;
          const canonical = FORM_TO_CANONICAL[key];
          const field = canonical
            ? t(`checkout.buyerDetails.${canonical === 'zipCode' ? 'zipCodeOptional' : canonical}`)
            : key;
          const type = String(d?.type || '');
          const ctx = d?.ctx || {};
          if (type.includes('too_short') || ctx.min_length != null) {
            perField[key] = t('checkout.buyerDetails.tooShort',
              { field, min: ctx.min_length ?? 1 });
          } else if (type.includes('too_long') || ctx.max_length != null) {
            perField[key] = t('checkout.buyerDetails.tooLong',
              { field, max: ctx.max_length });
          } else {
            perField[key] = t('checkout.buyerDetails.fieldInvalid', { field });
          }
        }
      }
      if (Object.keys(perField).length) {
        setFieldErrors(perField);
      } else {
        setError(detail
          ? t('checkout.buyerDetails.invalid')
          : t('checkout.errorGeneric'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel?.(); }}>
      <DialogContent className="sm:max-w-md" data-testid="buyer-details-dialog">
        <DialogHeader>
          <DialogTitle className="text-base md:text-lg">
            {t('checkout.buyerDetails.title')}
          </DialogTitle>
          <DialogDescription className="text-sm">
            {/* Never claim "we already have your name and contact details" while
                the latest response says one of them is missing. */}
            {profileOwned.length > 0
              ? t('checkout.buyerDetails.subtitleIncomplete')
              : t('checkout.buyerDetails.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="bd-type" className="text-sm">
              {t('checkout.buyerDetails.identityType')}
            </Label>
            <select
              id="bd-type"
              data-testid="buyer-details-identity-type"
              value={form.identity_type}
              onChange={set('identity_type')}
              className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="passport">{t('checkout.buyerDetails.passport')}</option>
              <option value="tckn">{t('checkout.buyerDetails.tckn')}</option>
            </select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bd-number" className="text-sm">
              {t('checkout.buyerDetails.identityNumber')}
            </Label>
            <Input id="bd-number" data-testid="buyer-details-identity-number"
                   value={form.identity_number} onChange={set('identity_number')}
                   autoComplete="off" required minLength={5} maxLength={32} />
            {fieldErrors.identity_number && (
              <p className="text-xs text-destructive"
                 data-testid="buyer-details-identity-number-error">
                {fieldErrors.identity_number}
              </p>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="bd-address" className="text-sm">
              {t('checkout.buyerDetails.registrationAddress')}
            </Label>
            <Input id="bd-address" data-testid="buyer-details-address"
                   value={form.registration_address} onChange={set('registration_address')}
                   autoComplete="street-address" required minLength={5} maxLength={200} />
            {fieldErrors.registration_address && (
              <p className="text-xs text-destructive"
                 data-testid="buyer-details-address-error">
                {fieldErrors.registration_address}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="bd-city" className="text-sm">
                {t('checkout.buyerDetails.city')}
              </Label>
              <Input id="bd-city" data-testid="buyer-details-city"
                     value={form.city} onChange={set('city')}
                     autoComplete="address-level2" required maxLength={60} />
              {fieldErrors.city && (
                <p className="text-xs text-destructive"
                   data-testid="buyer-details-city-error">{fieldErrors.city}</p>
              )}
            </div>
            {needsCountry && (
              <div className="space-y-1.5">
                <Label htmlFor="bd-country" className="text-sm">
                  {t('checkout.buyerDetails.country')}
                </Label>
                <select
                  id="bd-country"
                  data-testid="buyer-details-country"
                  value={form.country}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, country: e.target.value }));
                    setFieldErrors((p) => (p.country ? { ...p, country: undefined } : p));
                  }}
                  required
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                >
                  <option value="">{t('checkout.buyerDetails.countryHint')}</option>
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
                {fieldErrors.country && (
                  <p className="text-xs text-destructive"
                     data-testid="buyer-details-country-error">{fieldErrors.country}</p>
                )}
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="bd-zip" className="text-sm">
                {t('checkout.buyerDetails.zipCodeOptional')}
              </Label>
              <Input id="bd-zip" data-testid="buyer-details-zip"
                     value={form.zip_code} onChange={set('zip_code')}
                     autoComplete="postal-code" maxLength={16} />
              {fieldErrors.zip_code && (
                <p className="text-xs text-destructive"
                   data-testid="buyer-details-zip-error">{fieldErrors.zip_code}</p>
              )}
            </div>
          </div>

          <p className="flex items-start gap-2 text-xs text-muted-foreground">
            <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />
            <span data-testid="buyer-details-privacy">
              {t('checkout.buyerDetails.privacy')}
            </span>
          </p>

          {error && (
            <p className="text-sm text-destructive" data-testid="buyer-details-error">{error}</p>
          )}
          {serverMissing !== null && effectiveMissing.length > 0 && (
            <div
              className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="buyer-details-missing"
            >
              <p className="font-medium">
                {t('checkout.buyerDetails.stillMissing', {
                  fields: effectiveMissing.map(labelFor).join(', '),
                })}
              </p>
              {profileOwned.length > 0 && (
                <>
                  <p className="mt-1" data-testid="buyer-details-profile-owned">
                    {t(nameBlocked
                      ? 'checkout.buyerDetails.profileOwnedName'
                      : 'checkout.buyerDetails.profileOwned', {
                      fields: profileOwned.map(labelFor).join(', '),
                    })}
                  </p>
                  {nameBlocked ? (
                    <div className="mt-3 space-y-2" data-testid="buyer-details-name-correction">
                      <p className="text-xs">{t('checkout.buyerDetails.nameCorrectionHint')}</p>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label htmlFor="bd-first-name" className="text-xs">
                            {t('checkout.buyerDetails.name')}
                          </Label>
                          <Input id="bd-first-name" data-testid="buyer-details-first-name"
                                 value={nameForm.first_name}
                                 onChange={(e) => setNameForm(
                                   (f) => ({ ...f, first_name: e.target.value }))}
                                 autoComplete="given-name" maxLength={60} />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="bd-last-name" className="text-xs">
                            {t('checkout.buyerDetails.surname')}
                          </Label>
                          <Input id="bd-last-name" data-testid="buyer-details-last-name"
                                 value={nameForm.last_name}
                                 onChange={(e) => setNameForm(
                                   (f) => ({ ...f, last_name: e.target.value }))}
                                 autoComplete="family-name" maxLength={60} />
                        </div>
                      </div>
                      {nameError && (
                        <p className="text-xs text-destructive"
                           data-testid="buyer-details-name-error">{nameError}</p>
                      )}
                      <Button type="button" size="sm" variant="outline"
                              data-testid="buyer-details-name-save"
                              disabled={nameSaving || !nameForm.first_name.trim()
                                        || !nameForm.last_name.trim()}
                              onClick={submitName}>
                        {nameSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {t('checkout.buyerDetails.nameSave')}
                      </Button>
                    </div>
                  ) : (
                    <a
                      className="mt-2 inline-block font-medium underline"
                      data-testid="buyer-details-profile-action"
                      href={`#/contact?topic=buyer-identity&payment_id=${encodeURIComponent(paymentId)}`}
                    >
                      {t('checkout.buyerDetails.profileOwnedAction')}
                    </a>
                  )}
                </>
              )}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="ghost" onClick={onCancel}
                    data-testid="buyer-details-cancel">
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={saving} data-testid="buyer-details-submit">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('checkout.buyerDetails.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default BuyerDetailsDialog;
